import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { getVerificationAnalyticsQueryTemplatesSelector } from 'dashboard/redux/selectors/analytics-query-templates';
import { RootState } from 'dashboard/redux/store';
import {
  archiveAnalyticsQueryTemplate,
  getAnalyticsQueryTemplates,
} from 'dashboard/redux/thunk-actions/analytics-query-templates';
import DashboardVerificationAnalyticsQueryTemplatesView from 'dashboard/views/DashboardVerificationAnalyticsQueryTemplatesView/DashboardVerificationAnalyticsQueryTemplatesView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardVerificationAnalyticsQueryTemplateRoute = (props: PropsFromRedux) => {
  const { templates } = props;

  const {
    loading: loadingQueryTemplates,
    error: errorLoadingQueryTemplates,
    callback: getQueryTemplates,
  } = useApiCallback(async () => {
    await props.getAnalyticsQueryTemplates();
  }, [props.getAnalyticsQueryTemplates]);

  useApiErrorHandler({
    error: errorLoadingQueryTemplates,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load query templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: archivingQueryTemplate,
    error: archivingQueryTemplateError,
    callback: archiveQueryTemplate,
  } = useApiCallback(async (id: string) => {
    await props.archiveAnalyticsQueryTemplate({ id });
    await props.getAnalyticsQueryTemplates();
  }, []);
  useApiErrorHandler({
    error: archivingQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to archive query template.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  useEffect(() => {
    (async () => {
      await getQueryTemplates();
    })();
  }, [getQueryTemplates]);

  return (
    <DashboardVerificationAnalyticsQueryTemplatesView
      templates={templates}
      loading={loadingQueryTemplates || archivingQueryTemplate}
      handleArchive={archiveQueryTemplate}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  templates: getVerificationAnalyticsQueryTemplatesSelector(state),
});

const mapDispatchToProps = {
  getAnalyticsQueryTemplates,
  showMessage,
  archiveAnalyticsQueryTemplate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardVerificationAnalyticsQueryTemplateRoute);
